import React from 'react'
import Transition from './Transition'
import { CSSTransitionProps, CSSTransitionClassNames } from './types'
import { addClass, removeClass } from './utils/dom'
import { forceReflow } from './utils/reflow'

type TransitionType = 'appear' | 'enter' | 'exit'
type PhaseType = 'base' | 'active' | 'done'

interface AppliedClasses {
  appear: Record<PhaseType, string | undefined>
  enter: Record<PhaseType, string | undefined>
  exit: Record<PhaseType, string | undefined>
}

/**
 * A transition component inspired by CSS transitions and animations.
 * It's built upon the Transition component, so it inherits all of its props.
 *
 * CSSTransition applies a pair of class names during the `appear`, `enter`,
 * and `exit` states of the transition.
 *
 * This is a TypeScript port of react-transition-group's CSSTransition component,
 * optimized for React 19 compatibility.
 */
class CSSTransition<
  RefElement extends HTMLElement | undefined = undefined,
> extends React.Component<CSSTransitionProps<RefElement>> {
  static defaultProps = {
    classNames: '',
  }

  private appliedClasses: AppliedClasses = {
    appear: {} as Record<PhaseType, string | undefined>,
    enter: {} as Record<PhaseType, string | undefined>,
    exit: {} as Record<PhaseType, string | undefined>,
  }

  onEnter = (maybeNode: any, maybeAppearing: any) => {
    const [node, appearing] = this.resolveArguments(maybeNode, maybeAppearing)
    this.removeClasses(node, 'exit')
    this.addClass(node, appearing ? 'appear' : 'enter', 'base')

    if (this.props.onEnter) {
      this.props.onEnter(maybeNode, maybeAppearing)
    }
  }

  onEntering = (maybeNode: any, maybeAppearing: any) => {
    const [node, appearing] = this.resolveArguments(maybeNode, maybeAppearing)
    const type = appearing ? 'appear' : 'enter'
    this.addClass(node, type, 'active')

    if (this.props.onEntering) {
      this.props.onEntering(maybeNode, maybeAppearing)
    }
  }

  onEntered = (maybeNode: any, maybeAppearing: any) => {
    const [node, appearing] = this.resolveArguments(maybeNode, maybeAppearing)
    const type = appearing ? 'appear' : 'enter'
    this.removeClasses(node, type)
    this.addClass(node, type, 'done')

    if (this.props.onEntered) {
      this.props.onEntered(maybeNode, maybeAppearing)
    }
  }

  onExit = (maybeNode: any) => {
    const [node] = this.resolveArguments(maybeNode)
    this.removeClasses(node, 'appear')
    this.removeClasses(node, 'enter')
    this.addClass(node, 'exit', 'base')

    if (this.props.onExit) {
      this.props.onExit(maybeNode)
    }
  }

  onExiting = (maybeNode: any) => {
    const [node] = this.resolveArguments(maybeNode)
    this.addClass(node, 'exit', 'active')

    if (this.props.onExiting) {
      this.props.onExiting(maybeNode)
    }
  }

  onExited = (maybeNode: any) => {
    const [node] = this.resolveArguments(maybeNode)
    this.removeClasses(node, 'exit')
    this.addClass(node, 'exit', 'done')

    if (this.props.onExited) {
      this.props.onExited(maybeNode)
    }
  }

  // when prop `nodeRef` is provided `node` is excluded
  resolveArguments = (
    maybeNode: any,
    maybeAppearing?: any
  ): [HTMLElement | null, boolean?] => {
    return this.props.nodeRef
      ? [
          (this.props.nodeRef as React.RefObject<RefElement>)
            .current as HTMLElement | null,
          maybeNode,
        ] // here `maybeNode` is actually `appearing`
      : [maybeNode, maybeAppearing] // `findDOMNode` was used in original, but we avoid it
  }

  getClassNames = (type: TransitionType) => {
    const { classNames } = this.props
    const isStringClassNames = typeof classNames === 'string'
    const prefix = isStringClassNames && classNames ? `${classNames}-` : ''

    const baseClassName = isStringClassNames
      ? `${prefix}${type}`
      : (classNames as CSSTransitionClassNames)?.[type]

    const activeClassName = isStringClassNames
      ? `${baseClassName}-active`
      : (classNames as CSSTransitionClassNames)?.[`${type}Active`]

    const doneClassName = isStringClassNames
      ? `${baseClassName}-done`
      : (classNames as CSSTransitionClassNames)?.[`${type}Done`]

    return {
      baseClassName,
      activeClassName,
      doneClassName,
    }
  }

  addClass(node: HTMLElement | null, type: TransitionType, phase: PhaseType) {
    let className = this.getClassNames(type)[`${phase}ClassName`]
    const { doneClassName } = this.getClassNames('enter')

    if (type === 'appear' && phase === 'done' && doneClassName) {
      className = className ? `${className} ${doneClassName}` : doneClassName
    }

    // This is to force a repaint,
    // which is necessary in order to transition styles when adding a class name.
    if (phase === 'active') {
      if (node) forceReflow(node)
    }

    if (className) {
      this.appliedClasses[type][phase] = className
      addClass(node, className)
    }
  }

  removeClasses(node: HTMLElement | null, type: TransitionType) {
    const {
      base: baseClassName,
      active: activeClassName,
      done: doneClassName,
    } = this.appliedClasses[type]

    this.appliedClasses[type] = {} as Record<PhaseType, string | undefined>

    if (baseClassName) {
      removeClass(node, baseClassName)
    }
    if (activeClassName) {
      removeClass(node, activeClassName)
    }
    if (doneClassName) {
      removeClass(node, doneClassName)
    }
  }

  render() {
    const { classNames: _, ...props } = this.props

    return (
      <Transition<RefElement>
        {...props}
        onEnter={this.onEnter as any}
        onEntered={this.onEntered as any}
        onEntering={this.onEntering as any}
        onExit={this.onExit as any}
        onExiting={this.onExiting as any}
        onExited={this.onExited as any}
      />
    )
  }
}

export default CSSTransition
